<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import type { VoterMapPoint, VoterSummary } from '$lib/stores/voters';
  import { pinSvg, pinSvgOutreach } from '$lib/mapColors';
  import { outreachLabel } from '$lib/outreach';

  export let voters: VoterMapPoint[];
  // The map's active view mode, so the row dot matches the marker the
  // volunteer just tapped. Defaults to 'support' so every existing caller
  // (and every test that omits the prop) keeps today's Support-colour dots.
  export let viewMode: 'support' | 'coverage' | 'uncontacted' | 'outreach' = 'support';

  const dispatch = createEventDispatcher<{ pick: string; close: void }>();

  const PARTY_LABELS: Record<string, string> = {
    DEM: 'D', REP: 'R', UNA: 'U', LIB: 'L',
  };

  function fullName(v: VoterMapPoint): string {
    return `${v.first_name ?? ''} ${v.last_name ?? ''}`.trim() || '(unnamed)';
  }

  // Render the actual map-pin SVG so the dot beside each name carries the
  // same color the marker uses in the active view mode: outreach state in
  // Outreach mode, support status (with its contact-state size cue)
  // everywhere else. pinSvg and pinSvgOutreach both read only fields
  // VoterMapPoint already has.
  function dotSvg(v: VoterMapPoint): string {
    if (viewMode === 'outreach') return pinSvgOutreach(v);
    return pinSvg(v as unknown as VoterSummary);
  }

  // Short labels for non-spoke contact results, so a canvasser can see at a
  // glance who in a multi-voter household was already reached.
  const RESULT_LABELS: Record<string, string> = {
    not_home: 'Not home',
    refused: 'Refused',
    moved: 'Moved',
    wrong_address: 'Wrong addr',
  };

  // "Spoke 4/5" when we have a support level, "Spoke" when spoke but no level,
  // a short result label for other outcomes, and nothing for never-contacted.
  function lastContactLabel(v: VoterMapPoint): string | null {
    const r = v.latest_contact_result;
    if (!r) return null;
    if (r === 'spoke') {
      return v.latest_support_level != null ? `Spoke ${v.latest_support_level}/5` : 'Spoke';
    }
    return RESULT_LABELS[r] ?? null;
  }
</script>

<button class="backdrop" on:click={() => dispatch('close')} aria-label="Close voter picker" />

<section class="sheet" role="dialog" aria-modal="true" aria-label="Voters at this address">
  <header class="head">
    <h2>{voters.length} voters at this address</h2>
    <button class="close-btn" on:click={() => dispatch('close')} aria-label="Close">✕</button>
  </header>
  <ul class="voter-list">
    {#each voters as v (v.id)}
      <li>
        <button class="voter-row" class:dnc={v.do_not_contact} on:click={() => dispatch('pick', v.id)}>
          {#if v.do_not_contact}
            <span class="dnc-mark" aria-label="Do not contact" title="Do not contact">⊘</span>
          {:else}
            <span class="dot" aria-hidden="true">{@html dotSvg(v)}</span>
          {/if}
          <span class="name">{fullName(v)}</span>
          {#if v.do_not_contact}<span class="dnc-tag">DNC</span>{/if}
          {#if v.newly_registered}<span class="new-tag">NEW</span>{/if}
          {#if v.petition_signed}<span class="sign-tag" aria-label="Signed petition" title="Signed petition">✍</span>{/if}
          {#if lastContactLabel(v)}<span class="last-contact" class:spoke={v.latest_contact_result === 'spoke'}>{lastContactLabel(v)}</span>{/if}
          {#if v.text_outreach}<span class="text-outreach">Text: {outreachLabel(v.text_outreach)}</span>{/if}
          {#if v.party}<span class="party party-{v.party.toLowerCase()}">{PARTY_LABELS[v.party] ?? v.party}</span>{/if}
          {#if v.age != null}<span class="age">{v.age}</span>{/if}
        </button>
      </li>
    {/each}
  </ul>
</section>

<style>
  .backdrop {
    position: fixed; inset: 0; background: rgba(0,0,0,.3);
    z-index: 900; border: none; cursor: default;
  }
  .sheet {
    position: fixed; bottom: 0; left: 0; right: 0;
    background: white; border-radius: 1rem 1rem 0 0;
    padding: 1rem 1rem;
    padding-bottom: calc(2rem + env(safe-area-inset-bottom));
    z-index: 901;
    max-height: 50dvh; overflow-y: auto;
    box-shadow: 0 -4px 20px rgba(0,0,0,.15);
  }
  .head { display: flex; align-items: center; justify-content: space-between; }
  .head h2 { margin: 0; font-size: 1rem; }
  .close-btn {
    background: none; border: none; font-size: 1.1rem; cursor: pointer;
    min-width: 44px; min-height: 44px;
  }
  .voter-list { list-style: none; padding: 0; margin: 0.5rem 0 0; }
  .voter-row {
    display: flex; align-items: center; flex-wrap: wrap; gap: 10px;
    width: 100%; padding: 12px 8px; border: none; background: transparent;
    border-bottom: 1px solid #f3f4f6; cursor: pointer; text-align: left;
    font-size: 0.95rem;
    min-height: 44px;
  }
  .voter-row:hover { background: #f9fafb; }
  .voter-row:last-child { border-bottom: none; }
  .dot {
    width: 18px; height: 18px;
    display: inline-flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .dnc-mark {
    font-size: 1rem;
    color: #D62E2E;
    line-height: 1;
    width: 12px; flex-shrink: 0;
    text-align: center;
    font-weight: 700;
  }
  .dnc-tag {
    font-size: 0.65rem;
    background: #FBE0E0;
    color: #991B1B;
    border: 1px solid #D62E2E;
    padding: 1px 6px;
    border-radius: 999px;
    font-weight: 700;
    letter-spacing: 0.06em;
  }
  .new-tag {
    font-size: 0.65rem;
    background: var(--sun-soft, #FFF4BF);
    color: var(--sun-ink, #8A6B00);
    border: 1px solid var(--sun-bright, #E5B240);
    padding: 1px 6px;
    border-radius: 999px;
    font-weight: 700;
    letter-spacing: 0.06em;
  }
  .sign-tag {
    font-size: 0.85rem;
    color: var(--leaf, #2e7d32);
    margin-left: 2px;
  }
  .voter-row.dnc .name { color: #6b7280; }
  .name { flex: 1; font-weight: 500; }
  .party {
    font-size: 0.75rem; padding: 2px 6px; border-radius: 999px;
    background: #eee; color: #333;
  }
  /* Standard US party colors at chip strength: lighter bg, stronger text. */
  .party-dem { background: #DBE6FF; color: #1B4FE0; }
  .party-rep { background: #FBE0E0; color: #B11E1E; }
  .party-lib { background: #FFF4BF; color: #8A6B00; }
  .party-gre { background: #D9F2E5; color: #0E7D4B; }
  .party-una { background: #F3F4F6; color: #4B5563; }
  .last-contact {
    font-size: 0.65rem;
    background: #F3F4F6;
    color: #4B5563;
    border: 1px solid #D1D5DB;
    padding: 1px 6px;
    border-radius: 999px;
    font-weight: 600;
    white-space: nowrap;
  }
  .last-contact.spoke {
    background: #D9F2E5;
    color: #0E7D4B;
    border-color: #0E7D4B;
  }
  .text-outreach {
    font-size: 0.65rem;
    background: #EEF2FF;
    color: #3730A3;
    border: 1px solid #C7D2FE;
    padding: 1px 6px;
    border-radius: 999px;
    font-weight: 600;
    white-space: nowrap;
  }
  .age { font-size: 0.8rem; color: #6b7280; min-width: 20px; text-align: right; }
</style>
